import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { track } from '@farmersdog/analytics';
import {
  PATH_SIGNUP_ME,
  PATH_SIGNUP_PETS,
  PATH_SIGNUP_RECIPES,
} from '@farmersdog/constants/paths';
import {
  useMountNextDoorPageView,
  useMountAmazonTagPageView,
  useMountGa4PageView,
  useMountAudacyPageView,
  useMountPostiePageView,
  mountSpotifyPageView,
  mountSnapchatPageView,
  mountRedditPageVisit,
  mountAppLovinPageView,
  mountTikTokViewContent,
} from '@farmersdog/pixels';

import { segmentTrackEvents } from '../../analytics';

interface UseHandlePageViewArgs {
  isFirstPetsPage: boolean;
  email: string | undefined;
}

const SNAPCHAT_PAGEVIEW_ROUTES = [
  PATH_SIGNUP_ME,
  PATH_SIGNUP_PETS,
  PATH_SIGNUP_RECIPES,
];

const REDDIT_PAGEVIEW_ROUTES = [
  PATH_SIGNUP_ME,
  PATH_SIGNUP_PETS,
  PATH_SIGNUP_RECIPES,
];

const APPLOVIN_PAGEVIEW_ROUTES = [
  PATH_SIGNUP_ME,
  PATH_SIGNUP_PETS,
  PATH_SIGNUP_RECIPES,
];

const TIKTOK_PAGEVIEW_ROUTES = [PATH_SIGNUP_ME, PATH_SIGNUP_RECIPES];

export function useHandlePageView({
  isFirstPetsPage,
  email,
}: UseHandlePageViewArgs): void {
  const { pathname } = useLocation();

  useMountGa4PageView();
  useMountAudacyPageView();
  useMountNextDoorPageView({ isFirstPetsPage });
  useMountAmazonTagPageView({ isFirstPetsPage });
  useMountPostiePageView();

  // for pageview functions that should fire when the path (not querystring) changes
  useEffect(() => {
    if (SNAPCHAT_PAGEVIEW_ROUTES.includes(pathname)) {
      mountSnapchatPageView();
    }
    if (REDDIT_PAGEVIEW_ROUTES.includes(pathname)) {
      mountRedditPageVisit();
    }
    if (APPLOVIN_PAGEVIEW_ROUTES.includes(pathname)) {
      mountAppLovinPageView();
    }
    mountSpotifyPageView(pathname);
  }, [pathname]);

  // TODO: Move Nextdoor and Amazon pageviews to this useEffect
  useEffect(() => {
    if (TIKTOK_PAGEVIEW_ROUTES.includes(pathname) || isFirstPetsPage) {
      const eventId = uuidv4();

      void mountTikTokViewContent({
        email,
        event_id: eventId,
      });
      // call segment track with a special event to deduplicate tiktok events in S2S integration
      track(segmentTrackEvents.tiktok_page_view, {
        email,
        eventId,
      });
    }

    // We only want to track pathname/routing variable changes
    // TODO: The only other dependency is email, not sure why we wouldn't want
    // to track changes to that as well?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isFirstPetsPage]);

  // if we need to fire pageviews on querystring changes as well (ie. different signup flow cards) we can create a different effect that includes search in the dependency array
}
